/**
 * 사이드바 접기 버튼. 미루미와 같이 16px 아이콘, 6px 여백, 둥근 사각이다.
 * 사이드가 열려 있으면 사이드 오른쪽 끝에 두고, 접히면 레일 하단에 둔다.
 */
import { Tooltip, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { PanelLeft16Regular, PanelLeftContract16Regular, Search16Regular } from "@fluentui/react-icons";
import { CHROME_TOGGLE_TOP_PX } from "@/desktop/window-chrome";
import { modKey } from "@/lib/format";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";

const useStyles = makeStyles({
  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    padding: "6px",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    flexShrink: 0,
    transitionProperty: "opacity, transform, background-color, color",
    transitionDuration: "200ms",
    transitionTimingFunction: "ease-out",
    ":hover": {
      color: tokens.colorNeutralForeground1,
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  floating: {
    position: "absolute",
    zIndex: 4,
    top: `${CHROME_TOGGLE_TOP_PX}px`,
    left: "var(--chrome-toggle-left)",
  },
  enter: {
    opacity: 0,
    transform: "translateX(-8px)",
    pointerEvents: "none",
  },
  shown: {
    opacity: 1,
    transform: "translateX(0)",
  },
});

export function ChromeToggle({ revealed = true, floating = true }: { revealed?: boolean; floating?: boolean }) {
  const styles = useStyles();
  const collapsed = useCatalogStore((s) => s.navCollapsed);
  const label = collapsed ? "사이드바 열기" : "사이드바 접기";
  const Icon = collapsed ? PanelLeft16Regular : PanelLeftContract16Regular;

  return (
    <Tooltip content={`${label} (${modKey}B)`} relationship="label" positioning="below">
      <button
        type="button"
        className={mergeClasses(styles.button, floating && styles.floating, revealed ? styles.shown : styles.enter)}
        aria-label={label}
        aria-expanded={!collapsed}
        aria-controls="app-nav"
        data-chrome-toggle=""
        data-no-drag=""
        onClick={() => useCatalogStore.getState().setNavCollapsed(!useCatalogStore.getState().navCollapsed)}
      >
        <Icon />
      </button>
    </Tooltip>
  );
}

/** 명령 팔레트를 여는 검색. 펼친 사이드바에서는 접기 버튼 왼쪽에 둔다. */
export function ChromeSearch() {
  const styles = useStyles();
  const label = `검색 (${modKey}K)`;
  return (
    <Tooltip content={label} relationship="label" positioning="below">
      <button
        type="button"
        className={styles.button}
        aria-label={label}
        data-chrome-search=""
        data-no-drag=""
        onClick={() => useProjectStore.getState().openDialog("palette")}
      >
        <Search16Regular />
      </button>
    </Tooltip>
  );
}
